/**
 * specs/product-kernel/dashboard-ukrainian-localization.md
 *
 * Ukrainian dictionary. Follows the WoWyrd family's "80% ASD-STE100" writing rule: one idea
 * per sentence, short, active voice, one word per meaning — the same term every time
 * ("правило" for rule, "оператор" for operator, "пропозиція" for proposal, etc.), never a
 * synonym swapped in for variety. A key missing here falls back to `en.ts` (`useT()`'s own
 * contract) — never leave a key half-translated; add it here when the English is added.
 */

export const uk: Record<string, string> = {
  // ---- common ----
  'common.loading': 'Завантаження...',
  'common.cancel': 'Скасувати',
  'common.submit': 'Надіслати',
  'common.submitting': 'Надсилання...',
  'common.notesOptional': 'Примітки (необов’язково)',
  'common.notesRequired': 'Примітки (обов’язково)',
  'common.notesPlaceholderHelps': 'Чому ця зміна потрібна — це допоможе оператору, який схвалює.',
  'common.continue': 'Продовжити',
  'common.operatorApiKeyPlaceholder': 'Ключ API оператора',
  'common.operatorKeyRequiredTitle': 'Потрібен ключ оператора',
  'common.forgetOperatorKey': 'Забути ключ оператора',
  'common.operatorKeyInvalid': 'Цей ключ не розпізнано або його відкликано. Перевірте й спробуйте ще раз.',
  'common.operatorSessionSigningIn': 'Входимо…',
  'blocks.operatorKeyDescription':
    'Створення блокування записує, хто це зробив, через реальну ідентичність оператора — увійдіть за ключем оператора, щоб продовжити.',
  'author.operatorKeyDescription':
    'Запис огляду обґрунтованості правила потребує реальної ідентичності оператора — увійдіть за ключем оператора, щоб продовжити.',
  'cases.operatorKeyDescription':
    'Запис рішення огляду кейсу потребує реальної ідентичності оператора — увійдіть за ключем оператора, щоб продовжити.',
  'common.notEnoughData': 'Ще недостатньо даних',
  'common.outcome': 'Результат',
  'common.approve': 'Схвалити',
  'common.reject': 'Відхилити',
  'common.proposedBy': 'Запропонував',
  'common.approvedBy': 'Схвалив',
  'common.you': 'Ви',
  'common.notes': 'Примітки',
  'common.actions': 'Дії',
  'common.cannotApproveOwnProposal': 'Ви не можете схвалити або відхилити власну пропозицію',
  'common.clientOperator': 'оператор клієнта',
  'common.wowyrdOperator': 'оператор WoWyrd',
  'common.signedInAsPrefix': 'Увійшов як',

  // ---- decision labels ----
  'decision.all': 'Усі',
  'decision.allow': 'Дозволити',
  'decision.review': 'Перевірка',
  'decision.block': 'Блокувати',

  // ---- shell ----
  'shell.subtitleDefault': 'Контроль ризику перед транзакцією',
  'shell.navRisk': 'Ризик',
  'shell.live': 'Активно',
  'shell.loading': 'Завантаження...',
  'shell.langToggleLabel': 'Мова',

  // ---- header / tab nav ----
  'header.title': 'Запобігання шахрайству',
  'header.subtitle': 'Оцінка ризику перед транзакцією, лише спостереження · пояснюваний набір правил',
  'header.modeLabel': 'Режим',
  // Founder (2026-11-02): одна назва на один режим скрізь у дашборді — "Примусовий режим" це
  // термін глосарію (той самий, що й у enforcementMode.enforce нижче); цей бейдж раніше
  // показував "Блокування" для того самого режиму.
  'header.modeEnforce': 'Примусовий',
  'header.modeObserve': 'Спостереження',
  'header.modeAdvisory': 'Дорадчий',
  'header.modesExplainer':
    'WoWyrd працює у двох режимах: спостереження (оцінює й радить, нічого не блокує) або примусовий (рішення повертається до виконання платежу, і ваша система діє на його основі, щоб зупинити ризикові платежі).',
  'header.liveFeed': 'Жива стрічка',
  'header.reconnecting': 'Повторне підключення',
  'header.evaluated': 'оцінено',
  'header.blocked': 'заблоковано',
  'nav.ariaLabel': 'Розділи ризику',
  'tabs.feed': 'Стрічка транзакцій',
  'tabs.blocks': 'Заблоковані ідентифікатори',
  'tabs.velocity': 'Швидкість',
  'tabs.reasons': 'Причини',
  'tabs.coverage': 'Покриття',
  'tabs.cases': 'Справи',
  'tabs.kpis': 'KPI',
  'tabs.author': 'Автор',
  'tabs.calibration': 'Калібрування',
  'tabs.actionGates': 'Шлюзи дій',
  'tabs.webhooks': 'Вебхуки',
  'tabs.performance': 'Продуктивність',

  // ---- feed ----
  'feed.statTotal': 'Усього',
  'feed.statAllow': 'Дозволено',
  'feed.statReview': 'На перевірці',
  // Рішення засновника 2026-10-30 (передано через колегу): «Заблоковано» незалежно від режиму
  // роботи — число завжди показує реальний висновок правил (recommended_decision); чи це
  // застосовано насправді, видно окремо через feed.enforcementModeLabel нижче.
  'feed.statBlock': 'Заблоковано',
  'feed.enforcementModeLabel': 'Режим',
  'feed.enforcementModeTooltip':
    'Чи ця інсталяція справді блокує транзакції, чи лише рекомендує, що заблокувала б.',
  'enforcementMode.observe': 'Лише спостереження — нічого не блокується насправді',
  'enforcementMode.advisory': 'Дорадчий режим — нічого не блокується насправді',
  'enforcementMode.enforce': 'Примусовий режим — схвалені дії блокуються насправді',
  'feed.live': 'Активно',
  'feed.reconnecting': 'Повторне підключення...',
  'feed.colDecision': 'Рішення',
  'feed.colScore': 'Оцінка',
  'feed.colIp': 'IP',
  'feed.colCustomer': 'Клієнт',
  'feed.colFactors': 'Фактори',
  'feed.colLatency': 'Затримка',
  'feed.colTime': 'Час',
  'feed.colAmount': 'Сума',
  'feed.colType': 'Тип',
  'feed.colCountry': 'Країна',
  'feed.colReason': 'Основна причина',
  'feed.detailLabel': 'Деталі',
  'feed.waitingForTransactions': 'Очікування транзакцій...',
  'feed.connectingToFeed': 'Підключення до стрічки...',
  'feed.showing': 'Показано {{from}}–{{to}} з {{total}}',
  'feed.prev': 'Назад',
  'feed.next': 'Далі',
  'feed.wouldBlock': 'заблокував би',
  'feed.wouldBlockTooltip': 'Ідентифікатор у списку блокування — ця оцінка працювала лише в режимі спостереження/рекомендації, тому жорсткого блокування не було',
  'feed.recommendedPrefix': 'рекомендовано: {{decision}}',
  'feed.recommendedTooltip': 'Що насправді визначив механізм правил — ця оцінка працювала лише в режимі спостереження/рекомендації, тому колонка "Рішення" завжди показує "Дозволено"',
  'feed.blockedFactor': 'заблоковано',

  // ---- blocks ----
  'blocks.typeIp': 'IP-адреса',
  'blocks.typeCustomer': 'ID клієнта',
  'blocks.typeDevice': 'Відбиток пристрою',
  'blocks.filterAll': 'Усі ({{total}})',
  'blocks.addBlock': '+ Додати блокування',
  'blocks.noBlockedIdentifiers': 'Немає заблокованих ідентифікаторів',
  'blocks.colType': 'Тип',
  'blocks.colValue': 'Значення',
  'blocks.colReason': 'Причина',
  'blocks.colCreatedBy': 'Створив',
  'blocks.colExpires': 'Термін дії',
  'blocks.permanent': 'Постійно',
  'blocks.unblock': 'Розблокувати',
  'blocks.page': 'Сторінка {{page}} з {{totalPages}} (усього {{total}})',
  'blocks.addNewBlock': 'Додати нове блокування',
  'blocks.fieldType': 'Тип',
  'blocks.fieldValue': 'Значення',
  'blocks.valuePlaceholderIdentifier': 'ідентифікатор',
  'blocks.fieldReason': 'Причина',
  'blocks.reasonManualBlock': 'Ручне блокування',
  'blocks.reasonBankDecline': 'Відмова банку',
  'blocks.reasonChargeback': 'Чарджбек',
  'blocks.reasonHighVelocity': 'Висока швидкість запитів',
  'blocks.reasonVpnTor': 'VPN/Tor',
  'blocks.fieldTtl': 'Термін дії, годин (0 = постійно)',
  'blocks.blocking': 'Блокування...',
  'blocks.block': 'Блокувати',

  // ---- velocity ----
  'velocity.waitingForTraffic': 'Очікування даних трафіку…',
  'velocity.avgReqS': 'Сер. запитів/с (останні 10с)',
  'velocity.blockRate': 'Частка блокувань (останні 10с)',
  'velocity.dataPoints': 'Точки даних',
  'velocity.live': 'Активно',
  'velocity.paused': 'Призупинено',
  'velocity.chartTitle': 'Швидкість запитів (останні 60 секунд)',
  'velocity.seriesRequests': 'Запитів/с',
  'velocity.seriesBlocked': 'Блоковано/с',

  // ---- reasons ----
  'reasons.noRulesTriggered': 'Жодне правило ще не спрацювало.',
  'reasons.distinctRulesTriggered': 'Унікальних правил спрацювало',
  'reasons.eventsInView': 'Подій у перегляді',
  'reasons.feedStatus': 'Стан стрічки',
  'reasons.live': 'Активно',
  'reasons.paused': 'Призупинено',
  'reasons.topTriggeredRules': 'Найчастіші правила',
  'reasons.aggregatedFrom': 'Зведено з {{count}} останніх оцінок, що зберігаються в браузері — не історичний запит. Для звіту за весь період використайте',
  'reasons.colRule': 'Правило',
  'reasons.colReasonCode': 'Код причини',
  'reasons.colSource': 'Джерело',
  'reasons.colTriggers': 'Спрацювань',
  'reasons.colAvgWeight': 'Сер. вага',
  'reasons.colPctOfEvents': '% від подій',

  // ---- coverage (CoveragePanel.tsx) ----
  'coverage.title': 'Покриття набору правил',
  'coverage.subtitle':
    'Усі правила, які WoWyrd може застосувати, і скільки разів кожне спрацювало у цьому вікні браузера. Правило з нульовою кількістю спрацювань не зламане — воно просто ще не спрацювало.',
  'coverage.statTotalRules': 'Усього правил',
  'coverage.statSeen': 'Спрацювало в цьому вікні',
  'coverage.statNotYetSeen': 'Ще не спрацювало',
  'coverage.statUnavailable': 'Недоступні',
  'coverage.colRule': 'Правило',
  'coverage.colReasonCode': 'Код причини',
  'coverage.colStatus': 'Стан',
  'coverage.notYetSeen': 'Ще не спрацювало в цьому вікні',
  'coverage.seenCount': 'Спрацювало {{count}} разів у цьому вікні',

  // ---- cases ----
  'cases.statusTruePositive': 'Правильне спрацювання',
  'cases.statusFalsePositive': 'Хибне спрацювання',
  'cases.statusEscalated': 'Ескальовано',
  'cases.actionAllow': 'Дозволити',
  'cases.actionHold': 'Утримати',
  'cases.actionStepUp': 'Додаткова перевірка',
  'cases.actionThrottle': 'Обмежити',
  'cases.actionDenyCandidate': 'Кандидат на відмову',
  'cases.actionReview': 'Перевірка',
  'cases.noOpenCases': 'Немає відкритих справ — нічого не чекає на перевірку.',
  'cases.noResolvedCases': 'Ще немає вирішених справ.',
  'cases.viewOpen': 'Відкриті',
  'cases.viewResolved': 'Вирішені',
  'cases.colDecision': 'Рішення',
  'cases.colRecommended': 'Рекомендація',
  'cases.colScore': 'Оцінка',
  'cases.colCustomer': 'Клієнт',
  'cases.customerNotProvided': 'ідентифікатор не надано',
  'cases.customerRedacted': 'вилучено згідно з політикою зберігання',
  'cases.view': 'Перегляд',
  'cases.detailTitle': 'Деталі справи',
  'cases.detailBack': '← До списку справ',
  'cases.detailReasons': 'Причини',
  'cases.detailReview': 'Висновок аналітика',
  'cases.detailNoReview': 'Висновок ще не зафіксовано.',
  'cases.detailTimeline': 'Історія рахунку (інші позначені події цього рахунку)',
  'cases.detailTimelineEmpty': 'Немає інших позначених подій для цього рахунку.',
  'cases.exportHtml': 'Експорт HTML',
  'cases.exportJson': 'Експорт JSON',
  'cases.exportFailed': 'Не вдалося експортувати — спробуйте ще раз.',
  'cases.detailLoadFailed': 'Не вдалося завантажити цю справу.',
  'cases.colFactors': 'Фактори',
  'cases.colEvaluated': 'Оцінено',
  'cases.colReview': 'Перевірка',
  'cases.colActions': 'Дії',
  'cases.block': 'Блокувати',
  'cases.review': 'Перевірити',
  'cases.anonymizedIp': '— (анонімізовано)',
  'cases.recordReviewDecision': 'Записати рішення перевірки',
  'cases.escalate': 'Передати далі',
  'cases.notesWhyUndocumented': 'Чому цей результат — незадокументоване рішення не є пояснюваним.',
  'cases.submitReview': 'Надіслати перевірку',
  'cases.customer': 'Клієнт:',
  'cases.ip': 'IP:',
  'cases.factorsCount': 'Факторів ({{count}}):',

  // ---- kpis ----
  'kpis.falsePositiveRate': 'Частка хибних спрацювань',
  'kpis.falsePositiveRateHelp': 'підтверджено аналітиком, зі справ на перевірці',
  'kpis.preFlaggedRate': 'Частка попереднього виявлення',
  'kpis.preFlaggedRateHelp': 'підтверджене шахрайство, яке WoWyrd вже позначив раніше',
  'kpis.samplesOne': '{{count}} приклад',
  'kpis.samplesMany': '{{count}} прикладів',
  'kpis.decisionVolumeTrend': 'Тенденція обсягу рішень',
  'kpis.noEvaluationsInWindow': 'У цьому періоді немає оцінок.',
  'kpis.seriesAllow': 'Дозволено',
  'kpis.seriesReview': 'На перевірці',
  'kpis.seriesBlock': 'Блоковано',

  // ---- author ----
  'author.description': 'Обґрунтованість правил — частота спрацювання, частка хибних спрацювань за підтвердженими справами та застарілість. Записуйте періодичну перевірку для документування нагляду.',
  'author.noActiveRules': 'Немає активних правил для перевірки.',
  'author.colRule': 'Правило',
  'author.colFireRate': 'Частота спрацювання',
  'author.colFalsePositiveRate': 'Частка хибних спрацювань',
  'author.colDaysSinceChange': 'Днів від зміни',
  'author.colLastReview': 'Остання перевірка',
  'author.colActions': 'Дії',
  'author.weight': 'вага {{weight}}',
  'author.neverReviewed': 'Ще не перевірено',
  'author.review': 'Перевірити',
  'author.outcomeSound': 'Обґрунтоване',
  'author.outcomeNeedsRecalibration': 'Потребує калібрування',
  'author.outcomeNeedsRetirement': 'Потребує відключення',
  'author.recordSoundnessReview': 'Записати перевірку обґрунтованості — {{name}}',
  'author.notesWhyUndocumented': 'Чому цей результат — незадокументований висновок не є справжньою перевіркою.',
  'author.submitReview': 'Надіслати перевірку',

  // ---- calibration / rules ----
  'calibration.operatorKeyDescription': 'Калібрування правил доступне лише з індивідуальним ключем оператора, окремим від звичайного доступу до панелі — його видають і інженерам WoWyrd, і операторам клієнта.',
  'rules.title': 'Правила',
  'rules.description': 'Активні правила виявлення шахрайства. Пропозиція нової ваги не змінює її одразу — інший оператор повинен спочатку схвалити.',
  'rules.noActiveRules': 'Немає активних правил',
  'rules.colRule': 'Правило',
  'rules.colReasonCode': 'Код причини',
  'rules.colWeight': 'Вага',
  'rules.colSuggestion': 'Пропозиція',
  'rules.colActions': 'Дії',
  'rules.proposeCalibration': 'Запропонувати калібрування',
  'rules.proposeCalibrationTitle': 'Пропозиція калібрування — {{name}} (зараз {{weight}})',
  'rules.newWeightLabel': 'Нова вага (0–100)',
  'rules.submitProposal': 'Надіслати пропозицію',
  'pendingCalibration.title': 'Пропозиції калібрування на розгляді',
  'pendingCalibration.description': 'Схвалення застосовує зміну ваги та записує захищений від втручання рядок аудиту. Ви не можете схвалити або відхилити власну пропозицію, і вирішити зміну може лише інший оператор з тієї ж організації, що й той, хто її запропонував — пропозицію оператора клієнта повинен схвалити оператор клієнта, пропозицію оператора WoWyrd — оператор WoWyrd.',
  'pendingCalibration.noPendingProposals': 'Немає пропозицій на розгляді',
  'pendingCalibration.colRule': 'Правило',
  'pendingCalibration.colProposedWeight': 'Запропонована вага',

  // ---- calibration history (CalibrationHistory.tsx) — specs/control-api/
  // rule-weight-calibration.md's "Round 3" (2026-11-01) ----
  'calibrationHistory.title': 'Історія',
  'calibrationHistory.description': 'Кожна застосована зміна калібрування — захищений від втручання запис, лише для додавання. Тільки для внутрішніх операторів WoWyrd.',
  'calibrationHistory.noHistory': 'Застосованих змін ще немає',
  'calibrationHistory.colRule': 'Правило',
  'calibrationHistory.colWeightChange': 'Зміна ваги',
  'calibrationHistory.colApprovedAt': 'Схвалено',
  'calibrationHistory.colMode': 'Режим',
  'calibrationHistory.colEffectiveAt': 'Діє з',
  'calibrationHistory.modeSingleOperator': 'Один оператор',
  'calibrationHistory.modeDualControl': 'Подвійний контроль',
  'calibrationHistory.notYetConfirmed': 'Ще не підтверджено',
  'calibrationHistory.chainBroken': 'Перевірка захисту від втручання не пройшла — цю історію могли змінити. Зверніться до адміністратора.',
  'calibrationHistory.forbiddenForClientOperator': 'Цей розділ історії доступний лише для внутрішніх операторів WoWyrd. Клієнтське представлення тих самих даних — у розділі застосованих змін звіту доказів.',

  // ---- action gates ----
  'actionGates.operatorKeyDescription': 'Контроль шлюзів дій доступний лише з індивідуальним ключем оператора, тим самим, що й для калібрування правил — його видають і інженерам WoWyrd, і операторам клієнта.',
  'actionGates.title': 'Шлюзи дій',
  'actionGates.description': 'Чи дозволено рекомендації "кандидат на відмову" стати реальним, автономним блокуванням — незалежно від режиму виконання і додатково до нього. Пропозиція зміни не застосовує її одразу — інший оператор повинен спочатку схвалити.',
  'actionGates.noGatedActionTypes': 'Немає типів дій під контролем',
  'actionGates.colActionType': 'Тип дії',
  'actionGates.colCurrentState': 'Поточний стан',
  'actionGates.colActions': 'Дії',
  'actionGates.denyCandidateToBlock': 'Кандидат на відмову → реальне блокування',
  'actionGates.enabled': 'Увімкнено',
  'actionGates.disabled': 'Вимкнено',
  'actionGates.proposeChange': 'Запропонувати зміну',
  'actionGates.proposeChangeTitle': 'Пропозиція зміни — {{name}} (зараз {{state}})',
  'actionGates.requestedState': 'Запитаний стан: {{state}}',
  'actionGates.requestedStateLabel': 'Запитано увімкнення',
  'actionGates.submitProposal': 'Надіслати пропозицію',
  'pendingActionGates.title': 'Пропозиції шлюзів дій на розгляді',
  'pendingActionGates.description': 'Схвалення застосовує зміну шлюзу та записує захищений від втручання рядок аудиту (власний, окремий ланцюжок хешів — не той самий, що для калібрування). Ви не можете схвалити або відхилити власну пропозицію, і вирішити зміну може лише інший оператор з тієї ж організації, що й той, хто її запропонував.',
  'pendingActionGates.noPendingProposals': 'Немає пропозицій на розгляді',
  'pendingActionGates.colActionType': 'Тип дії',
  'pendingActionGates.colRequestedState': 'Запитаний стан',
  'pendingActionGates.colApprovalMode': 'Схвалення',
  'pendingActionGates.singleOperatorMode': 'Режим одного оператора',
  'pendingActionGates.dualOperatorMode': 'Потрібно {{count}} окремих затверджувачів',

  // ---- step-up config ----
  'stepUp.title': 'Налаштування додаткової перевірки',
  'stepUp.description': 'Лише для перегляду — поточні, схвалені перевизначення коду причини та додаткове порогове значення оцінки, яке може дати результат "додаткова перевірка". Зміна будь-якого з них відбувається лише через API (поки що не тут).',
  'stepUp.scoreThreshold': 'Порогове значення оцінки',
  'stepUp.thresholdSetText': 'Будь-яка оцінка в діапазоні "на перевірці" ≥ {{threshold}} дає результат "додаткова перевірка", незалежно від коду причини.',
  'stepUp.thresholdNotSet': 'Не встановлено — цей шлях вимкнено; застосовується лише перевизначення коду причини нижче (або типові налаштування).',
  'stepUp.noApprovedOverrides': 'Немає схвалених перевизначень коду причини — застосовуються типові списки без змін.',
  'stepUp.colReasonCode': 'Код причини',
  'stepUp.colMappedAction': 'Призначена дія',
  'stepUp.colProposedAt': 'Запропоновано',

  // ---- webhooks ----
  'webhooksTab.operatorKeyDescription': 'Керування кінцевими точками вебхуків доступне лише з індивідуальним ключем оператора, тим самим, що й для калібрування правил і шлюзів дій.',
  'webhooks.title': 'Вебхуки',
  'webhooks.description': 'Кінцеві точки клієнта, які отримують сповіщення про вирішення справ і шлюзів дій — лише зміст рішення, без персональних даних за задумом (окрім поля "перевірив", яке заповнює користувач вручну). Реєстрація кінцевої точки діє одразу — для цього елемента керування схвалення не потрібне, за результатами огляду цієї специфікації.',
  'webhooks.registerEndpoint': 'Зареєструвати кінцеву точку',
  'webhooks.eventCaseReviewResolved': 'Справу вирішено',
  'webhooks.eventActionApprovalResolved': 'Схвалення дії вирішено',
  'webhooks.secretRotated': 'Секрет оновлено',
  'webhooks.endpointRegistered': 'Кінцеву точку зареєстровано',
  'webhooks.secretShownOnceWarning': 'Цей підписувальний секрет показано лише зараз — отримати його знову неможливо. Збережіть його, перш ніж закрити цю панель.',
  'webhooks.done': 'Готово',
  'webhooks.noEndpointsRegistered': 'Немає зареєстрованих кінцевих точок вебхуків',
  'webhooks.colEndpoint': 'Кінцева точка',
  'webhooks.colEvents': 'Події',
  'webhooks.colStatus': 'Стан',
  'webhooks.colActions': 'Дії',
  'webhooks.disabledSince': 'Вимкнено з {{date}}',
  'webhooks.consecutiveFailureOne': '{{count}} послідовна помилка',
  'webhooks.consecutiveFailureMany': '{{count}} послідовних помилок',
  'webhooks.healthy': 'Справний',
  'webhooks.hideHistory': 'Сховати історію',
  'webhooks.history': 'Історія',
  'webhooks.reenable': 'Увімкнути знову',
  'webhooks.rotateSecret': 'Оновити секрет',
  'webhooks.delete': 'Видалити',
  'webhooks.loadingDeliveryHistory': 'Завантаження історії доставок...',
  'webhooks.noDeliveriesYet': 'Для цієї кінцевої точки ще немає доставок.',
  'webhooks.colEvent': 'Подія',
  'webhooks.colResult': 'Результат',
  'webhooks.colAttempts': 'Спроб',
  'webhooks.colWhen': 'Коли',
  'webhooks.delivered': 'Доставлено',
  'webhooks.failed': 'Помилка',
  'webhooks.copied': 'Скопійовано',
  'webhooks.copy': 'Копіювати',
  'webhooks.registerNewEndpoint': 'Зареєструвати нову кінцеву точку вебхука',
  'webhooks.urlLabel': 'URL',
  'webhooks.eventsLabel': 'Події',
  'webhooks.registering': 'Реєстрація...',
  'webhooks.register': 'Зареєструвати',
  'webhooks.deleteConfirm': 'Видалити кінцеву точку вебхука {{url}}? Це також видалить історію доставок, і дію неможливо скасувати.',
  'webhooks.rotateConfirm': 'Оновити підписувальний секрет для {{url}}? Це діє одразу — доставки не пройдуть перевірку підпису, доки отримувач не оновить новий секрет.',

  // ---- performance ----
  'performance.serviceHealth': 'Стан сервісу',
  'performance.updated': 'Оновлено {{time}}',
  'performance.refresh': 'Оновити',
  'performance.apiServer': 'Сервер API',
  'performance.ebpfTcProgram': 'Програма eBPF TC',
  'performance.redis': 'Redis',
  'performance.postgresql': 'PostgreSQL',
  'performance.wsClients': 'Клієнтів WS:',
  'performance.status': 'Стан:',
  'performance.loadingHealthData': 'Завантаження даних про стан…',
  'performance.healthDataUnavailable': 'Дані про стан недоступні',
  'performance.dashboardConnection': 'З’єднання панелі',
  'performance.webSocketStatus': 'Стан WebSocket',
  'performance.connected': 'Підключено',
  'performance.reconnectingEllipsis': 'Повторне підключення…',
  'performance.reconnectCount': 'Кількість перепідключень',
  'performance.connectionInterruptions': 'перерв з’єднання',
  'performance.stable': 'стабільно',
  'performance.ebpfMapUtilization': 'Використання карт eBPF',
  'performance.ipBlacklist': 'Чорний список IP',
  'performance.customerBlacklist': 'Чорний список клієнтів',
  'performance.deviceBlacklist': 'Чорний список пристроїв',
  'performance.kernelCounters': 'Лічильники ядра',
  'performance.packetsProcessed': 'Оброблено пакетів',
  'performance.sinceLastRestart': 'усього від останнього перезапуску',
  'performance.packetsDroppedKernel': 'Скинуто пакетів (ядро)',
  'performance.tcActShotCount': 'кількість TC_ACT_SHOT',
  'performance.blockRate': 'Частка блокувань',

  // ---- alert banner ----
  'alert.chargeback': 'Чарджбек',
  'alert.fraudBlock': 'Блокування шахрайства',
  'alert.dismiss': 'Закрити сповіщення',

  // ---- license banner ----
  'license.unlicensed': 'Файл ліцензії не знайдено',
  'license.invalid': 'Файл ліцензії недійсний',
  'license.expired': 'Ліцензія закінчилася {{date}}',
  'license.expiringSoon': 'Ліцензія діє до {{date}}',
  'license.active': 'Ліцензія для {{client}}',
  'license.tierSuffix': ' ({{tier}})',
  'license.offlineMode': 'Офлайн-режим — без зовнішніх запитів до джерел розвідки загроз',
  'license.usageNearLimit': '{{count}} / {{limit}} оцінок цього місяця — наближається до заявленого обсягу',
  'license.usageOverLimit': '{{count}} / {{limit}} оцінок цього місяця — перевищено заявлений обсяг',

  // ---- demo provenance banner (DemoProvenanceBanner.tsx) ----
  // specs/simulation-lab/demo-dataset-replay-and-provenance.md
  'demo.provenance.label': 'Демо-дані',
  'demo.provenance.tooltip':
    'Синтетичний трафік на основі набору даних PaySim. Автор: Едгар Лопес-Рохас. Ліцензія CC BY-SA 4.0 (kaggle.com/datasets/ealaxi/paysim1). Без реальних клієнтів і реальних транзакцій.',

  // ---- demo read-only controls (DemoReadOnlyNotice.tsx, Calibration.tsx, ActionGatesTab.tsx,
  // WebhooksTab.tsx, CasesQueue.tsx, BlockedIdentifiers.tsx) ----
  // specs/simulation-lab/demo-neobank-traffic.md
  'demo.readOnly.badge': 'Лише читання в демо',
  'demo.readOnly.settingsMessage':
    'Лише читання в демо — нижче приклад налаштувань, а не реальна конфігурація клієнта. Дії запису вимкнено в публічному демо.',
  'demo.readOnly.writeMessage': 'Лише читання в демо — дії запису вимкнено в публічному демо.',
};
